<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
      <div>
        <h1 class="text-2xl font-bold text-white">Notificaciones</h1>
        <p class="text-content-300 mt-1">Mantente informado sobre las actualizaciones importantes</p>
      </div>
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
        <button
          v-if="selectedNotifications.length > 0"
          @click="deleteSelectedNotifications"
          class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg button-interactive text-sm sm:text-base whitespace-nowrap"
        >
          <span class="sm:hidden">Eliminar ({{ selectedNotifications.length }})</span>
          <span class="hidden sm:inline">Eliminar seleccionadas ({{ selectedNotifications.length }})</span>
        </button>
        <button
          v-if="notifications.length > 0"
          @click="markAllAsRead"
          class="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg button-interactive text-sm sm:text-base whitespace-nowrap"
        >
          <span class="sm:hidden">Marcar como leídas</span>
          <span class="hidden sm:inline">Marcar todas como leídas</span>
        </button>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="bg-content-800 rounded-lg p-4">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div class="flex items-center justify-center sm:justify-start space-x-6 sm:space-x-4">
          <div class="text-center">
            <div class="text-2xl font-bold text-primary-400">{{ unreadCount }}</div>
            <div class="text-sm text-content-300">Sin leer</div>
          </div>
          <div class="text-center">
            <div class="text-2xl font-bold text-white">{{ notifications.length }}</div>
            <div class="text-sm text-content-300">Total</div>
          </div>
        </div>
        <div class="flex items-center justify-center sm:justify-end">
          <select
            v-model="filters.status"
            @change="loadNotifications"
            class="w-full sm:w-auto px-3 py-2 bg-content-700 border border-content-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">Todas</option>
            <option value="unread">Sin leer</option>
            <option value="read">Leídas</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <loader-icon class="h-8 w-8 text-primary-500 animate-spin"></loader-icon>
    </div>

    <!-- Empty State -->
    <div v-else-if="notifications.length === 0" class="text-center py-12">
      <bell-icon class="w-16 h-16 mx-auto text-content-400 mb-4" />
      <h3 class="text-lg font-medium text-white mb-2">No hay notificaciones</h3>
      <p class="text-content-300 mb-4">Cuando haya actualizaciones importantes, aparecerán aquí.</p>
      <router-link
        :to="{ name: 'dashboard' }"
        class="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg button-interactive"
      >
        Volver al inicio
      </router-link>
    </div>

    <!-- Notifications List -->
    <div v-else class="space-y-4">
      <!-- Select All -->
      <div v-if="notifications.length > 0" class="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-content-800 rounded-lg p-4 space-y-3 sm:space-y-0">
        <label class="flex items-center">
          <input
            type="checkbox"
            :checked="allSelected"
            @change="toggleSelectAll"
            class="rounded border-content-600 text-primary-600 focus:ring-primary-500 focus:ring-offset-0 bg-content-700"
          >
          <span class="ml-2 text-sm text-content-200">
            <span class="sm:hidden">
              {{ selectedNotifications.length === 0 ? 'Seleccionar todas' : `${selectedNotifications.length} seleccionadas` }}
            </span>
            <span class="hidden sm:inline">
              {{ selectedNotifications.length === 0 ? 'Seleccionar todas' : `${selectedNotifications.length} seleccionadas` }}
            </span>
          </span>
        </label>
        <div class="flex items-center justify-between sm:justify-end space-x-4">
          <span class="text-sm text-content-400">{{ notifications.length }} notificaciones</span>
          <button
            v-if="notifications.length > 0 && selectedNotifications.length === 0"
            @click="deleteAllNotifications"
            class="text-sm text-red-400 hover:text-red-300 button-interactive"
          >
            <span class="sm:hidden">Eliminar todas</span>
            <span class="hidden sm:inline">Eliminar todas</span>
          </button>
        </div>
      </div>

      <!-- Notification Items -->
      <div
        v-for="notification in notifications"
        :key="notification.id || Math.random()"
        class="bg-content-800 rounded-lg border border-content-600 hover:border-content-500 transition-colors"
        :class="{ 'border-primary-500 bg-content-750': !notification.isRead }"
      >
        <div class="p-4">
          <!-- Mobile Layout -->
          <div class="sm:hidden">
            <div class="flex items-start space-x-3 mb-3">
              <input
                type="checkbox"
                :value="notification.id"
                v-model="selectedNotifications"
                class="mt-1 rounded border-content-600 text-primary-600 focus:ring-primary-500 focus:ring-offset-0 bg-content-700 w-4 h-4"
              >
              <div
                class="flex-shrink-0 w-3 h-3 rounded-full mt-2"
                :class="notification.isRead ? 'bg-transparent' : 'bg-primary-500'"
              ></div>
            </div>
            
            <div class="space-y-3">
              <div>
                <h3 class="text-base font-medium text-white mb-2">{{ notification.title || 'Sin título' }}</h3>
                <p class="text-content-200 mb-3 leading-relaxed text-sm">{{ notification.message || 'Sin mensaje' }}</p>
              </div>
              
              <div class="flex flex-wrap items-center gap-2 text-sm">
                <span class="text-content-400">{{ formatDate(notification.createdAt) }}</span>
                <span v-if="notification.notificationType && notification.notificationType.name" class="px-2 py-1 bg-primary-600 text-primary-100 rounded-full text-xs">
                  {{ notification.notificationType.name }}
                </span>
                <span v-if="!notification.isRead" class="px-2 py-1 bg-orange-600 text-orange-100 rounded-full text-xs">
                  Nueva
                </span>
              </div>

              <div class="flex flex-col space-y-2">
                <button
                  v-if="getNotificationAction(notification)"
                  @click="navigateToNotificationTarget(notification)"
                  class="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg button-interactive"
                >
                  Ver detalles
                </button>
                <div class="flex justify-between">
                  <button
                    v-if="!notification.isRead"
                    @click="markAsRead(notification.id)"
                    class="flex items-center space-x-2 px-3 py-2 text-content-400 hover:text-primary-400 button-interactive text-sm"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Marcar como leída</span>
                  </button>
                  <button
                    @click="deleteNotification(notification.id)"
                    class="flex items-center space-x-2 px-3 py-2 text-content-400 hover:text-red-400 button-interactive text-sm"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Eliminar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Desktop Layout -->
          <div class="hidden sm:flex items-start space-x-3">
            <input
              type="checkbox"
              :value="notification.id"
              v-model="selectedNotifications"
              class="mt-1 rounded border-content-600 text-primary-600 focus:ring-primary-500 focus:ring-offset-0 bg-content-700 w-4 h-4"
            >
            
            <div
              class="flex-shrink-0 w-3 h-3 rounded-full mt-2"
              :class="notification.isRead ? 'bg-transparent' : 'bg-primary-500'"
            ></div>

            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <h3 class="text-lg font-medium text-white mb-2">{{ notification.title || 'Sin título' }}</h3>
                  <p class="text-content-200 mb-3 leading-relaxed">{{ notification.message || 'Sin mensaje' }}</p>
                  <div class="flex items-center space-x-4 text-sm">
                    <span class="text-content-400">{{ formatDate(notification.createdAt) }}</span>
                    <span v-if="notification.notificationType && notification.notificationType.name" class="px-2 py-1 bg-primary-600 text-primary-100 rounded-full text-xs">
                      {{ notification.notificationType.name }}
                    </span>
                    <span v-if="!notification.isRead" class="px-2 py-1 bg-orange-600 text-orange-100 rounded-full text-xs">
                      Nueva
                    </span>
                  </div>
                </div>
                <div class="flex items-center space-x-2 ml-4">
                  <button
                    v-if="getNotificationAction(notification)"
                    @click="navigateToNotificationTarget(notification)"
                    class="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg button-interactive flex items-center space-x-2"
                  >
                    <span>Ver detalles</span>
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    v-if="!notification.isRead"
                    @click="markAsRead(notification.id)"
                    class="p-2 text-content-400 hover:text-primary-400 button-interactive"
                    title="Marcar como leída"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                  <button
                    @click="deleteNotification(notification.id)"
                    class="p-2 text-content-400 hover:text-red-400 button-interactive"
                    title="Eliminar notificación"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="flex justify-center">
        <nav class="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
          <div class="flex items-center space-x-2 w-full sm:w-auto">
            <button
              :disabled="!pagination.hasPrevPage"
              @click="changePage(pagination.currentPage - 1)"
              class="flex-1 sm:flex-initial px-3 py-2 bg-content-700 border border-content-600 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed button-interactive text-sm sm:text-base"
            >
              <span class="sm:hidden">‹</span>
              <span class="hidden sm:inline">Anterior</span>
            </button>
            <button
              :disabled="!pagination.hasNextPage"
              @click="changePage(pagination.currentPage + 1)"
              class="flex-1 sm:flex-initial px-3 py-2 bg-content-700 border border-content-600 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed button-interactive text-sm sm:text-base"
            >
              <span class="sm:hidden">›</span>
              <span class="hidden sm:inline">Siguiente</span>
            </button>
          </div>
          <span class="px-4 py-2 text-content-200 text-sm sm:text-base text-center">
            <span class="sm:hidden">{{ pagination.currentPage }}/{{ pagination.totalPages }}</span>
            <span class="hidden sm:inline">Página {{ pagination.currentPage }} de {{ pagination.totalPages }}</span>
          </span>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import BellIcon from "../../../../icons/BellIcon";
import LoaderIcon from "../../../../icons/LoaderIcon";
import api from "../../../../api";

export default {
  name: "ClientNotificationsIndex",
  components: {
    BellIcon,
    LoaderIcon
  },
  data() {
    return {
      notifications: [],
      selectedNotifications: [],
      loading: false,
      unreadCount: 0,
      filters: {
        status: 'all'
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        hasNextPage: false,
        hasPrevPage: false
      }
    };
  },
  computed: {
    allSelected() {
      return this.notifications.length > 0 && this.selectedNotifications.length === this.notifications.length;
    }
  },
  async created() {
    try {
      await this.loadNotifications();
      await this.loadUnreadCount();
    } catch (error) {
      console.error('Error initializing notifications component:', error);
      this.$store.dispatch("errorSnack", "Error al cargar las notificaciones");
    }
  },
  methods: {
    async loadNotifications(page = 1) {
      this.loading = true;
      try {
        const params = {
          page,
          limit: 15
        };

        if (this.filters.status !== 'all') {
          params.unread = this.filters.status === 'unread' ? 'true' : 'false';
        }

        const response = await api.notifications.getNotifications(params);
        this.notifications = Array.isArray(response.notifications) ? response.notifications : [];
        this.pagination = response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          hasNextPage: false,
          hasPrevPage: false
        };
        this.selectedNotifications = [];
      } catch (error) {
        console.error('Error loading notifications:', error);
        this.notifications = [];
      } finally {
        this.loading = false;
      }
    },

    async loadUnreadCount() {
      try {
        const response = await api.notifications.getUnreadCount();
        this.unreadCount = response.unreadCount || response.count || 0;
      } catch (error) {
        console.error('Error loading unread count:', error);
        this.unreadCount = 0;
      }
    },

    async markAsRead(notificationId) {
      // Prevent multiple calls for the same notification
      const notification = this.notifications.find(n => n.id === notificationId);
      if (!notification || notification.isRead) {
        return;
      }

      try {
        await api.notifications.markAsRead(notificationId);

        // Update local state
        notification.isRead = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);

        this.$store.dispatch("successSnack", "Notificación marcada como leída");
      } catch (error) {
        console.error('Error marking notification as read:', error);
        this.$store.dispatch("errorSnack", "Error al marcar notificación");
      }
    },

    async markAllAsRead() {
      try {
        await api.notifications.markAllAsRead();
        
        // Update local state
        this.notifications = this.notifications.map(n => ({ ...n, isRead: true }));
        this.unreadCount = 0;
        
        this.$store.dispatch("successSnack", "Todas las notificaciones marcadas como leídas");
      } catch (error) {
        console.error('Error marking all as read:', error);
        this.$store.dispatch("errorSnack", "Error al marcar notificaciones");
      }
    },

    async deleteNotification(notificationId) {
      if (!confirm('¿Estás seguro de que quieres eliminar esta notificación?')) {
        return;
      }

      try {
        await api.notifications.deleteNotification(notificationId);
        
        // Remove from local state
        const notification = this.notifications.find(n => n.id === notificationId);
        if (notification && !notification.isRead) {
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
        
        this.notifications = this.notifications.filter(n => n.id !== notificationId);
        this.selectedNotifications = this.selectedNotifications.filter(id => id !== notificationId);
        
        this.$store.dispatch("successSnack", "Notificación eliminada");
      } catch (error) {
        console.error('Error deleting notification:', error);
        this.$store.dispatch("errorSnack", "Error al eliminar notificación");
      }
    },

    async deleteSelectedNotifications() {
      if (this.selectedNotifications.length === 0) return;
      
      if (!confirm(`¿Estás seguro de que quieres eliminar ${this.selectedNotifications.length} notificaciones?`)) {
        return;
      }

      try {
        // Count unread notifications that will be deleted
        const unreadToDelete = this.notifications.filter(n => 
          this.selectedNotifications.includes(n.id) && !n.isRead
        ).length;
        
        // Delete each selected notification
        await Promise.all(
          this.selectedNotifications.map(id => api.notifications.deleteNotification(id))
        );
        
        // Remove from local state
        this.notifications = this.notifications.filter(n => !this.selectedNotifications.includes(n.id));
        this.unreadCount = Math.max(0, this.unreadCount - unreadToDelete);
        this.selectedNotifications = [];
        
        this.$store.dispatch("successSnack", "Notificaciones eliminadas");
      } catch (error) {
        console.error('Error deleting notifications:', error);
        this.$store.dispatch("errorSnack", "Error al eliminar notificaciones");
      }
    },

    async deleteAllNotifications() {
      if (!confirm('¿Estás seguro de que quieres eliminar TODAS tus notificaciones? Esta acción no se puede deshacer.')) {
        return;
      }

      try {
        await api.notifications.deleteAllNotifications();
        
        // Clear local state
        this.notifications = [];
        this.selectedNotifications = [];
        this.unreadCount = 0;
        
        this.$store.dispatch("successSnack", "Todas las notificaciones eliminadas");
      } catch (error) {
        console.error('Error deleting all notifications:', error);
        this.$store.dispatch("errorSnack", "Error al eliminar notificaciones");
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedNotifications = [];
      } else {
        this.selectedNotifications = this.notifications.map(n => n.id);
      }
    },

    changePage(page) {
      this.loadNotifications(page);
    },

    async navigateToNotificationTarget(notification) {

      if (!notification || !notification.data) {
        console.warn('Invalid notification data');
        return;
      }

      const data = notification.data;

      if (!data.type || !data.opportunityId) {
        console.warn('Missing notification data properties');
        return;
      }

      try {
        // Mark as read if unread
        if (!notification.isRead) {
          await this.markAsRead(notification.id);
        } else {
        }

        if (data.type === 'opportunity') {
          this.$router.push({
            name: 'opportunityDetail',
            params: { id: String(data.opportunityId) }
          });
        }
      } catch (error) {
        console.error('Error navigating to notification target:', error);
        this.$store.dispatch("errorSnack", "Error al abrir los detalles");
      }
    },

    getNotificationAction(notification) {
      if (!notification || !notification.data) return false;
      const data = notification.data;
      return data && data.type === 'opportunity' && data.opportunityId;
    },

    formatDate(dateString) {
      if (!dateString) return 'Fecha desconocida';
      
      const date = new Date(dateString);
      
      // Check if date is invalid
      if (isNaN(date.getTime())) return 'Fecha inválida';
      
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 1) {
        return 'Hace unos minutos';
      } else if (diffInHours < 24) {
        return `Hace ${Math.floor(diffInHours)} horas`;
      } else if (diffInHours < 48) {
        return 'Ayer';
      } else {
        try {
          return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (error) {
          return date.toDateString();
        }
      }
    }
  }
};
</script>